import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ExternalLinks } from "@/constants/links";
import { PageRoute } from "@/constants/pages";
import { cancelAppointment, fetchStore } from "@/services/booking";
import { isSetAndNotEmpty } from "@/utils/helpers";

const STORE_SEARCH_PARAM_KEY = "store";
const LOCATION_SEARCH_PARAM_KEY = "location";

export function useStoreKey(code: string | undefined | null) {
  return ["stores", code];
}

export function useStoreQuery() {
  const [searchParams] = useSearchParams();

  const storeCode = searchParams.get("store") ?? localStorage.getItem("store");

  if (!storeCode) {
    document.location.href = ExternalLinks.LOCATIONS;
  }

  return useQuery({
    queryKey: useStoreKey(storeCode),
    queryFn: isSetAndNotEmpty(storeCode)
      ? () => fetchStore(storeCode)
      : skipToken,
  });
}

export function usePersistedStore() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const _store = searchParams.get(STORE_SEARCH_PARAM_KEY);
    const _location = searchParams.get(LOCATION_SEARCH_PARAM_KEY);

    const derivedStore = _store ?? _location;

    if (derivedStore) {
      localStorage.setItem("store", derivedStore);
      if (_store) {
        searchParams.delete(STORE_SEARCH_PARAM_KEY);
      }
      if (_location) {
        searchParams.delete(LOCATION_SEARCH_PARAM_KEY);
      }
      navigate(
        {
          pathname: PageRoute.APPOINTMENT_BOOK,
          search: searchParams.toString(),
        },
        {
          replace: true,
        },
      );
    }
  }, [navigate, searchParams]);
}

export const useCancelAppointmentMutation = () =>
  useMutation({
    mutationFn: cancelAppointment,
  });
